import { Box, Button, Checkbox, Grid, Typography } from '@mui/material'
import React, { useState } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { ClientAdvisorAtom, ClientAdvisorSelector } from '../../../../State/Advisory/ClientAdvisors.Atom';
import { AdvisorsLinkedToClientsResult } from '../../../../Component/Model/Dto/Advisory/Employee';
import { AdvisoryService } from '../../../../Component/Api/Advisory/AdvisoryService';
import { toast } from 'react-toastify';
import { AdvancedSupplierModuleRole, BEEAdvisoryRole, EEAdvisoryRole, StrategicRelationshipManagerRole, DataSupportRole, ProjectsRole } from '../../../../Component/Common/Constants/UserRoles';

interface AdvisorRoleSectionConfig {
    role: string;
    title: string;
    description: string;
    emptyMessage: string;
}

const ADVISOR_ROLE_SECTIONS: AdvisorRoleSectionConfig[] = [
    {
        role: BEEAdvisoryRole,
        title: 'BEE Advisors',
        description: 'The below BEE Advisors are associated to your registration. You may grant or revoke their access to your scorecards.',
        emptyMessage: 'There are no BEE Advisors associated to your registration.',
    },
    {
        role: EEAdvisoryRole,
        title: 'EE Advisors',
        description: 'The below EE Advisors are associated to your registration. You may grant or revoke their access to your EE Module data.',
        emptyMessage: 'There are no EE Advisors associated to your registration.',
    },
    {
        role: AdvancedSupplierModuleRole,
        title: 'Advanced Supplier Managers',
        description: 'The below Advanced Supplier Managers has read-only access to the Preferential Procurement element on your scorecards. You may grant or revoke their access to your scorecards.',
        emptyMessage: 'There are no Advanced Supplier Managers associated to your registration.',
    },
    {
        role: StrategicRelationshipManagerRole,
        title: 'Strategic Relationship Managers',
        description: 'The below Strategic Relationship Managers are associated to your registration. You may grant or revoke their access to your scorecards.',
        emptyMessage: 'There are no Strategic Relationship Managers associated to your registration.',
    },
    {
        role: DataSupportRole,
        title: 'Data Support',
        description: 'The below Data Support users are associated to your registration. You may grant or revoke their access to your scorecards.',
        emptyMessage: 'There are no Data Support users associated to your registration.',
    },
    {
        role: ProjectsRole,
        title: 'Projects',
        description: 'The below Project users are associated to your registration. You may grant or revoke their access to your scorecards.',
        emptyMessage: 'There are no Projects users associated to your registration.',
    },
];

const KNOWN_ADVISOR_ROLES = ADVISOR_ROLE_SECTIONS.map(section => section.role);

const StyledCheckbox = ({ disabled, checked, onChange }: { disabled?: boolean, checked: boolean; onChange: (event: React.ChangeEvent<HTMLInputElement>) => void }) => {
    return (
        <Checkbox
            sx={{
                '&.MuiCheckbox-root .MuiSvgIcon-root': {
                    border: '0.0625rem solid #505B60',
                },
                '&.Mui-checked .MuiSvgIcon-root': {
                    border: 'transparent',
                    backgroundColor: '#505B60',
                },
                '&.Mui-disabled .MuiSvgIcon-root': {
                    backgroundColor: '#d1d1d1',
                },
                '&.Mui-disabled .Mui-checked .MuiSvgIcon-root': {
                    color: '#888888',
                },
            }}
            disabled={disabled}
            checked={checked}
            onChange={onChange}
        />
    );
};

const AdvisorRoleTable = ({ employees, onCheckAll, onUpdateOne }: {
    employees: AdvisorsLinkedToClientsResult[];
    onCheckAll: (checked: boolean) => void;
    onUpdateOne: (employeeId: string, checked: boolean) => void;
}) => {
    const allChecked = employees.every(employee => employee.optIn);

    return (
        <Grid container spacing={1}>
            <Grid item xs={12}>
                <table key={employees.length} style={{ width: '100%' }}>
                    <thead>
                        <tr style={{ background: "var(--semantics-disabled, #E9ECEF)" }}>
                            <th>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <StyledCheckbox
                                        checked={allChecked}
                                        onChange={(e) => onCheckAll(e.target.checked)}
                                    />
                                    <Typography variant='body2' ml={'7px'} fontWeight={'bold'}>Name</Typography>
                                </Box>
                            </th>
                        </tr>
                    </thead>
                    <tbody style={{ backgroundColor: 'white' }}>
                        {employees.map((employee, index) => (
                            <tr key={index} style={{ borderBottom: '1px solid #C9C9C980', padding: '10px 0 10px 0' }}>
                                <td width="150px">
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <StyledCheckbox
                                            checked={employee.optIn}
                                            onChange={(e) => onUpdateOne(employee.employeeId, e.target.checked)}
                                        />
                                        <Typography variant='body2' ml={'7px'}>{employee.advisorName}</Typography>
                                    </Box>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Grid>
        </Grid>
    );
};

export const ClientAdvisors = () => {
    const bee123Employees = useRecoilValue(ClientAdvisorSelector);
    const setBEE123Employees = useSetRecoilState(ClientAdvisorAtom);

    const [allEmployees, setAllEmployees] = useState<AdvisorsLinkedToClientsResult[]>(
        bee123Employees?.filter(employee => KNOWN_ADVISOR_ROLES.includes(employee.roleName)) ?? []
    );
    const [isSaving, setIsSaving] = useState<boolean>(false);

    const handleCheckAllForRole = (role: string, checked: boolean) => {
        setAllEmployees(current => current.map(employee =>
            employee.roleName === role ? { ...employee, optIn: checked } : employee
        ));
    };

    const updateEmployeeOptIn = (role: string, employeeId: string, checked: boolean) => {
        setAllEmployees(current => current.map(employee =>
            employee.roleName === role && employee.employeeId === employeeId ? { ...employee, optIn: checked } : employee
        ));
    };

    const saveAdvisors = async () => {
        setIsSaving(true);
        try {
            const result = await AdvisoryService.updateClientAdvisorLinks(allEmployees);
            setBEE123Employees(result.data)
            toast.success("Advisors have been saved successfully");
        } catch (error: any) {
            toast.error(error)
            console.error(error);
        } finally {
            setIsSaving(false);
        }
    }

    return (
        <>
            {ADVISOR_ROLE_SECTIONS.map((section, index) => {
                const sectionEmployees = allEmployees.filter(employee => employee.roleName === section.role);

                return (
                    <React.Fragment key={section.role}>
                        {index > 0 && <Box mb={2} />}

                        <Typography variant="h4" color={"primary"}>{section.title}</Typography>

                        {sectionEmployees.length > 0 ? (
                            <>
                                <Typography variant="body2">
                                    <p>{section.description}</p>
                                </Typography>
                                <AdvisorRoleTable
                                    employees={sectionEmployees}
                                    onCheckAll={(checked) => handleCheckAllForRole(section.role, checked)}
                                    onUpdateOne={(employeeId, checked) => updateEmployeeOptIn(section.role, employeeId, checked)}
                                />
                            </>
                        ) : (
                            <Box style={{ textAlign: 'left', paddingTop: '10px' }}>
                                <Box>
                                    <Typography variant="body2">
                                        <p>{section.emptyMessage}</p>
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                        <hr style={{ marginTop: '20px', marginBottom: '20px', border: '1px solid #C9C9C980' }} />
                    </React.Fragment>
                );
            })}

            <Grid item xs={12} mt={1} display='flex'>
                {allEmployees.length > 0 &&
                    <Box mr={1} sx={{ width: 150 }}>
                        <Button variant='contained' color='primary' fullWidth onClick={() => saveAdvisors()} disabled={isSaving}>Save</Button>
                    </Box>
                }
            </Grid>
        </>
    )
}
